import { useCallback, useEffect, useRef, useState } from "react";

export interface DraggableBag {
    /** Spread onto the element that should MOVE. */
    moveProps: {
        style: React.CSSProperties;
    };
    /** Spread onto the grab handle (usually the header). Cursor becomes `move`. */
    handleProps: {
        onMouseDown: React.MouseEventHandler<HTMLElement>;
        style: React.CSSProperties;
    };
    /** Reset back to the initial anchor. Useful for "snap to corner" buttons. */
    reset: () => void;
}

/**
 * Drag-to-move hook. Anchored on initial mount — positions are tracked
 * as (x, y) translations relative to wherever the element lands from
 * regular layout. Works for the chat window because it's `position:fixed`
 * and initially at bottom-right; `translate()` shifts it from there.
 *
 * Kept local to the chat module rather than promoted to `@/lib` — the
 * only other draggable surface right now is the whiteboard, and that has
 * its own full-screen mode.
 */
export function useDraggable(): DraggableBag {
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragStart = useRef<{ pointerX: number; pointerY: number; initX: number; initY: number } | null>(null);

    const onMouseMove = useCallback((e: MouseEvent) => {
        const s = dragStart.current;
        if (!s) return;
        setOffset({
            x: s.initX + (e.clientX - s.pointerX),
            y: s.initY + (e.clientY - s.pointerY)
        });
    }, []);

    const onMouseUp = useCallback(() => {
        dragStart.current = null;
    }, []);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    const onMouseDown: React.MouseEventHandler<HTMLElement> = (e) => {
        // Left-click only, and don't start a drag over form controls inside
        // the header (close / minimize buttons).
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (target.closest("button, input, textarea, select, a")) return;
        dragStart.current = {
            pointerX: e.clientX,
            pointerY: e.clientY,
            initX: offset.x,
            initY: offset.y
        };
        e.preventDefault();
    };

    return {
        moveProps: {
            style: { transform: `translate(${offset.x}px, ${offset.y}px)` }
        },
        handleProps: {
            onMouseDown,
            style: { cursor: "move", userSelect: "none" }
        },
        reset: () => setOffset({ x: 0, y: 0 })
    };
}
